import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Filter, Search } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getExpiringLots, getInventoryItems, getInventoryLots } from '../../utils/inventoryApi';
import { listVendors } from '../../utils/vendorsApi';
import type { InventoryItem, InventoryLot, Vendor } from '../../types/inventory';
import { useEscapeKey } from '../../hooks/useEscapeKey';

type AggregatedRow = {
  key: string;
  catalog_type: string;
  catalog_item_id: string;
  name: string;
  unit_price?: string | null;
  last_buy_cost?: string | null;
  avg_buy_cost?: string | null;
  recent_receipts: Array<{
    received_date: string;
    unit_cost_received: string;
    vendor_id?: string | null;
    vendor_name?: string | null;
    lot_number: string;
  }>;
  on_hand: number;
  reserved: number;
  lot_count: number;
  expiring_soon_count: number;
};

export const InventoryStatus: React.FC = () => {
  const { user } = useAuth();
  const practiceId = user?.practice_id;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCategory = searchParams.get('category') || 'All';
  const setSelectedCategory = (category: string) => {
    const next = new URLSearchParams(searchParams);
    if (category === 'All') next.delete('category');
    else next.set('category', category);
    setSearchParams(next);
  };

  // Filter preference (not URL-driven) — match Billing Catalog approach.
  // Inventory Status is already scoped to stock-tracked items; filter by "has last buy price" is more useful.
  type BuyPriceFilter = 'all' | 'has' | 'none';
  const [buyPriceFilter, setBuyPriceFilter] = useState<BuyPriceFilter>(() => {
    const v = localStorage.getItem('inventory_status_buyPriceFilter');
    if (v === 'all' || v === 'has' || v === 'none') return v;
    // Back-compat: previously stored as boolean "has last buy only"
    const legacy = localStorage.getItem('inventory_status_hasLastBuyOnly');
    if (legacy === 'true') return 'has';
    return 'all';
  });

  const setBuyPriceFilterPersisted = (nextValue: BuyPriceFilter) => {
    setBuyPriceFilter(nextValue);
    localStorage.setItem('inventory_status_buyPriceFilter', nextValue);
    // Cleanup legacy key so we don't drift between two sources of truth.
    localStorage.removeItem('inventory_status_hasLastBuyOnly');
  };

  const [lots, setLots] = useState<InventoryLot[]>([]);
  const [expiringLots, setExpiringLots] = useState<InventoryLot[]>([]);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const filterMenuRef = useRef<HTMLDivElement>(null);

  useEscapeKey(() => setShowFilterMenu(false), showFilterMenu);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (showFilterMenu && filterMenuRef.current && !filterMenuRef.current.contains(target)) {
        setShowFilterMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [showFilterMenu]);

  useEffect(() => {
    if (!practiceId) return;
    const pid = practiceId;

    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const [allLots, expLots, invItems, vends] = await Promise.all([
          getInventoryLots(pid, { include_consumed: false }),
          getExpiringLots(pid, 60),
          getInventoryItems(pid),
          listVendors(pid),
        ]);
        if (cancelled) return;
        setLots(allLots);
        setExpiringLots(expLots);
        setItems(invItems);
        setVendors(vends);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load inventory');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [practiceId, refreshKey]);

  const itemByKey = useMemo(() => {
    const m = new Map<string, InventoryItem>();
    for (const it of items) {
      m.set(`${it.catalog_type}:${it.catalog_item_id}`, it);
    }
    return m;
  }, [items]);

  const vendorById = useMemo(() => {
    const m = new Map<string, Vendor>();
    for (const v of vendors) m.set(v.id, v);
    return m;
  }, [vendors]);

  const fmtMoney = (val?: string | null) => {
    if (!val) return null;
    const n = Number(val);
    if (Number.isNaN(n)) return val;
    return n.toFixed(2);
  };

  const rows = useMemo<AggregatedRow[]>(() => {
    const expiringSet = new Set(expiringLots.map((l) => l.id));

    const byKey = new Map<string, AggregatedRow & { _lots: InventoryLot[] }>();

    // Seed rows from stock-tracked items so they appear even with 0 lots/on-hand.
    for (const it of items) {
      const key = `${it.catalog_type}:${it.catalog_item_id}`;
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          catalog_type: it.catalog_type,
          catalog_item_id: it.catalog_item_id,
          name: it.name,
          unit_price: it.unit_price ?? null,
          last_buy_cost: null,
          avg_buy_cost: null,
          recent_receipts: [],
          _lots: [],
          on_hand: 0,
          reserved: 0,
          lot_count: 0,
          expiring_soon_count: 0,
        });
      }
    }

    for (const lot of lots) {
      const key = `${lot.catalog_type}:${lot.catalog_item_id}`;
      const item = itemByKey.get(key);
      const cur = byKey.get(key) || {
        key,
        catalog_type: lot.catalog_type,
        catalog_item_id: lot.catalog_item_id,
        name: item?.name || lot.catalog_item_id,
        unit_price: item?.unit_price ?? null,
        last_buy_cost: null,
        avg_buy_cost: null,
        recent_receipts: [],
        _lots: [],
        on_hand: 0,
        reserved: 0,
        lot_count: 0,
        expiring_soon_count: 0,
      };
      cur._lots.push(lot);
      cur.on_hand += Number(lot.quantity_on_hand || 0);
      cur.reserved += Number(lot.quantity_reserved || 0);
      cur.lot_count += 1;
      if (expiringSet.has(lot.id)) cur.expiring_soon_count += 1;
      byKey.set(key, cur);
    }

    const arr = Array.from(byKey.values()).map((r) => {
      const sorted = [...r._lots].sort((a, b) => (b.received_date || '').localeCompare(a.received_date || ''));
      const recent = sorted.slice(0, 5);
      const recentWithCost = recent.filter((x) => Number(x.unit_cost_received || 0) > 0);
      const lastWithCost = recentWithCost[0];
      const avg =
        recentWithCost.length > 0
          ? recentWithCost.reduce((sum, x) => sum + Number(x.unit_cost_received || 0), 0) / recentWithCost.length
          : null;

      const receipts = recent.map((x) => ({
        received_date: x.received_date,
        unit_cost_received: String(x.unit_cost_received),
        vendor_id: x.vendor_id || null,
        vendor_name: x.vendor_id ? vendorById.get(x.vendor_id)?.name || null : null,
        lot_number: x.lot_number,
      }));

      return {
        key: r.key,
        catalog_type: r.catalog_type,
        catalog_item_id: r.catalog_item_id,
        name: r.name,
        unit_price: r.unit_price,
        last_buy_cost: lastWithCost ? String(lastWithCost.unit_cost_received) : null,
        avg_buy_cost: avg != null ? String(avg.toFixed(2)) : null,
        recent_receipts: receipts,
        on_hand: r.on_hand,
        reserved: r.reserved,
        lot_count: r.lot_count,
        expiring_soon_count: r.expiring_soon_count,
      } as AggregatedRow;
    });
    // Prefer showing items that actually have stock, but still keep 0-stock visible.
    arr.sort((a, b) => (b.on_hand - a.on_hand) || a.name.localeCompare(b.name));
    return arr;
  }, [lots, expiringLots, itemByKey, vendorById, items]);

  const filteredRows = useMemo(() => {
    let out = rows;
    if (selectedCategory !== 'All') {
      const wanted =
        selectedCategory === 'Immunizations'
          ? 'immunization'
          : selectedCategory === 'Labs'
            ? 'diagnostic'
            : selectedCategory === 'Procedures'
              ? 'procedure'
              : selectedCategory === 'Treatments'
                ? 'treatment'
                : selectedCategory.toLowerCase();
      out = out.filter((r) => r.catalog_type.toLowerCase() === wanted);
    }
    if (buyPriceFilter !== 'all') {
      out = out.filter((r) => {
        const n = Number(r.last_buy_cost || 0);
        return buyPriceFilter === 'has' ? n > 0 : n <= 0;
      });
    }
    if (!searchTerm.trim()) return out;
    const term = searchTerm.toLowerCase();
    return out.filter((r) => {
      return (
        r.name.toLowerCase().includes(term) ||
        r.catalog_item_id.toLowerCase().includes(term) ||
        r.catalog_type.toLowerCase().includes(term)
      );
    });
  }, [rows, searchTerm, selectedCategory, buyPriceFilter]);

  if (!practiceId) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="text-sm text-gray-600">Select a practice to view inventory.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <span className="ml-3 text-sm text-gray-600">Loading inventory…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="text-sm text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search + Filters */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col gap-4">
          {/* Match Billing Catalog: category + search in the same block */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex gap-2 flex-wrap min-w-0">
              {['All', 'Product', 'Medication', 'Immunizations', 'Labs', 'Procedures', 'Treatments'].map((c) => (
                <button
                  key={c}
                  onClick={() => setSelectedCategory(c)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selectedCategory === c ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between gap-4 flex-1">
              <div className="relative flex-1 min-w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={() => setRefreshKey((x) => x + 1)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Billing-catalog style Filter button (below search) */}
          <div className="relative mt-1" ref={filterMenuRef}>
            <button
              onClick={() => setShowFilterMenu((v) => !v)}
              className="p-2 rounded-lg border bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 transition-colors"
              title="Filter options"
            >
              <Filter className="w-5 h-5" />
            </button>

            {showFilterMenu && (
              <div className="absolute top-full left-0 mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 min-w-56 overflow-hidden">
                <button
                  onClick={() => {
                    // Toggle between 'has' and 'none'
                    // When clicking "Only items with Last Buy price", set filter to 'has'
                    // When clicking "Only items with no buy price", set filter to 'none'
                    setBuyPriceFilterPersisted(buyPriceFilter === 'has' ? 'none' : 'has');
                    setShowFilterMenu(false);
                  }}
                  className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-700 ${buyPriceFilter === 'all' ? 'text-gray-200' : 'text-white font-semibold bg-gray-700/40'
                    }`}
                >
                  {buyPriceFilter === 'has' ? 'Only items with no buy price' : 'Only items with Last Buy price'}
                </button>

                <button
                  onClick={() => {
                    setBuyPriceFilterPersisted('all');
                    setShowFilterMenu(false);
                  }}
                  className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-700 ${buyPriceFilter === 'all' ? 'text-white font-semibold bg-gray-700/40' : 'text-gray-200'
                    }`}
                >
                  Show all items
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sell Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Buy</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Buy (last 5)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">On Hand</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reserved</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lots</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiring (60d)</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredRows.map((row) => (
              <tr
                key={row.key}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/settings/inventory/status/${row.catalog_type}/${row.catalog_item_id}`)}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <div className="text-sm font-medium text-gray-900">{row.name}</div>
                  <div className="text-xs text-gray-500">{row.catalog_item_id}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{row.catalog_type}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {row.unit_price ? `$${row.unit_price}` : '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {row.last_buy_cost ? `$${fmtMoney(row.last_buy_cost)}` : '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {row.avg_buy_cost ? `$${fmtMoney(row.avg_buy_cost)}` : '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.on_hand}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.reserved}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.lot_count}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.expiring_soon_count}</td>
              </tr>
            ))}
            {filteredRows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-6 py-10 text-center text-sm text-gray-500">
                  No stock-tracked items found. Mark a Product/Medication/Immunization as “Track as Inventory”, then refresh.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};


